import React from 'react';
import PropTypes from 'prop-types';
import Color from 'color';
import pickBy from 'lodash/pickBy';
import { Motion } from 'react-motion';
import { invertRange, springify } from '../../lib/Motion';
import { sortByContrast } from '../../lib/Color';
import { EMPTY_OBJECT, FULL_STYLE } from '../../constants';
import { withHover } from '../Hoverable';
import { Arrow, Check } from '../Icons';
import WorkingSpinner from './WorkingSpinner';
import { L1 } from '../Typography';
import Style from '../Style';

const SIZES = {
  small: {
    height: 3,
    borderWidth: 0.2,
    fontSize: '1.8rem',
    letterSpacing: 0,
    labelTop: 0,
  },
  large: {
    height: 5,
    borderWidth: 0.4,
    fontSize: '2.2rem',
    letterSpacing: '-0.15px',
    labelTop: '-0.1rem',
  },
};

class Button extends React.PureComponent {
  static propTypes = {
    baseColor: PropTypes.string,
    lightColor: PropTypes.string,
    darkColor: PropTypes.string,
    hover: PropTypes.bool,
    checked: PropTypes.bool,
    enabled: PropTypes.bool,
    onClick: PropTypes.func,
    border: PropTypes.bool,
    size: PropTypes.oneOf(Object.keys(SIZES)),
    style: PropTypes.object,
    arrow: PropTypes.bool,
    type: PropTypes.string,
    working: PropTypes.bool,
    children: PropTypes.node,
  };
  static defaultProps = {
    baseColor: Style.Colors.white,
    lightColor: Style.Colors.white,
    darkColor: Style.Colors.black,
    hover: false,
    checked: false,
    enabled: true,
    onClick: () => {},
    border: true,
    size: 'small',
    style: EMPTY_OBJECT,
    arrow: false,
    type: undefined,
    working: false,
    children: ' ',
  };

  constructor(props) {
    super(props);

    this.state = {
      checked: props.checked,
      labelMaxWidth: 0,
    };

    this.animatedDefault = {
      border: props.border ? 1 : 0,
      hover: props.hover && props.enabled ? 1 : 0,
      working: props.working ? 1 : 0,
      arrow: props.arrow ? 1 : 0,
      checked: props.checked ? 1 : 0,
    };
  }

  componentDidMount() {
    this.getMaxWidth();
  }

  componentWillReceiveProps(nextProps) {
    if (!this.props.checked && nextProps.checked) {
      this.getMaxWidth();
      this.setState({ checked: true });
    }
  }

  aniamtionResting = () => {
    if (!this.props.checked && this.state.checked) {
      this.setState({ checked: false });
    }
  };

  getMaxWidth = () => {
    let width = 0;
    if (this.labelEl) {
      width = this.labelEl.getBoundingClientRect().width;
    }
    this.setState({ labelMaxWidth: width });
  };

  render() {
    const {
      lightColor,
      darkColor,
      baseColor,
      hover,
      checked,
      enabled,
      onClick,
      border,
      size,
      style,
      arrow,
      type,
      working,
      children,
      ...otherProps
    } = this.props;

    let sizeParams = SIZES[size];
    if (arrow) {
      sizeParams = SIZES['large'];
    }

    const [colorB, colorA] = sortByContrast([lightColor, darkColor], baseColor);

    const { fontWeight, fontStyle, letterSpacing, lineHeight, fontSize, ...otherStyles } = style;

    let hoverFillColor = colorA;
    let workingFillColor = colorA;

    let hoverColor = colorB;
    let workingColor = colorB;
    let forgroundFillMulitplier = 1;

    let arrowPadding = 0;
    let arrowMultiplier = 0;
    if (arrow) {
      arrowPadding = 1;
      arrowMultiplier = 1;
      hoverFillColor = Color(colorB).alpha(0.1);
      forgroundFillMulitplier = 0;
    }

    if (!border) {
      hoverFillColor = 'transparent';
    }

    let buttonStyle = {
      overflow: 'hidden',
      position: 'relative',
      height: `${sizeParams.height}rem`,
      minWidth: `${sizeParams.height}rem`,
      borderRadius: '99999rem',
      borderStyle: 'solid',
      cursor: enabled ? 'pointer' : 'not-allowed',
      overflow: checked ? 'visible' : 'hidden',
      ...otherStyles,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
    };

    let labelStyle = pickBy(
      {
        fontWeight: fontWeight,
        fontStyle: fontStyle,
        letterSpacing: letterSpacing,
        lineHeight: lineHeight,
        fontSize: fontSize,
        margin: 0,
        position: 'relative',
        top: sizeParams.labelTop,
      },
      value => value !== undefined
    );

    let workingStyle = {
      maxWidth: `${sizeParams.height * 0.6}rem`,
    };

    let arrowStyle = {
      display: arrow ? 'block' : 'none',
      position: 'relative',
      top: '0.2rem',
    };

    const hovering = hover && enabled;
    const animatedCurrent = {
      border: border ? 1 : 0,
      hover: hovering ? 1 : 0,
      working: working ? 1 : 0,
    };
    const animatedCurrentChecked = {
      checked: this.props.checked ? 1 : 0,
    };
    const arrowOn = arrow && hovering && !this.state.checked && !working;
    const animatedCurrentArrow = {
      arrow: arrowOn ? 0.3 : 0,
    };

    return (
      <Motion
        defaultStyle={this.animatedDefault}
        style={{
          ...springify(animatedCurrent, {
            stiffness: hovering || working ? 180 : 100,
            damping: hovering || working ? 25 : 35,
          }),
          ...springify(animatedCurrentArrow, {
            stiffness: arrowOn ? 80 : 75,
            damping: arrowOn ? 0 : 20,
          }),
          ...springify(animatedCurrentChecked, {
            stiffness: this.props.checked ? 180 : 180,
            damping: this.props.checked ? 15 : 15,
          }),
        }}
        onRest={this.aniamtionResting}
      >
        {tween => {
          let tweenCheckedInverse = invertRange(tween.checked);
          let tweenWorkingInverse = invertRange(tween.working);
          let borderWidth = sizeParams.borderWidth * tween.border;

          // Animated properties.
          const buttonStyleMerged = {
            ...buttonStyle,
            borderWidth: `${borderWidth * tweenCheckedInverse}rem`,
            paddingTop: 0,
            paddingBottom: 0,
            paddingLeft: `${(sizeParams.height - borderWidth - arrowPadding) *
              tweenCheckedInverse *
              tween.border}rem`,
            paddingRight: `${(sizeParams.height - borderWidth) * tweenCheckedInverse}rem`,
            borderColor: Color(colorA)
              .alpha(tweenCheckedInverse)
              .string(),
            color: Color(colorA)
              .mix(Color(hoverColor), tween.hover * forgroundFillMulitplier)
              .mix(Color(workingColor), tween.working)
              .string(),
            backgroundColor: Color('transparent')
              .mix(Color(hoverFillColor), tween.hover)
              .mix(Color(workingFillColor), tween.working)
              .mix(Color('transparent'), tween.checked)
              .string(),
            maxWidth: undefined,
          };
          if (this.state.checked) {
            buttonStyleMerged.maxWidth = `calc((${this.state
              .labelMaxWidth}px + ${buttonStyleMerged.paddingLeft} + ${buttonStyleMerged.paddingRight}) * ${tweenCheckedInverse})`;
          }
          const labelStyleMerged = {
            fontFamily: 'Calibre',
            fontSize: sizeParams.fontSize,
            letterSpacing: sizeParams.letterSpacing,
            fontWeight: 600,
            ...labelStyle,
            opacity: tweenWorkingInverse * tweenCheckedInverse,
            whiteSpace: 'nowrap',
            display: 'flex',
            alignItems: 'center',
          };
          const workingContainerStyle = {
            ...FULL_STYLE,
            pointerEvents: 'none',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            transformOrigin: '50% 50%',
            transform: `scale(${0.8 + tweenWorkingInverse * 1})`,
          };
          const workingStyleMerged = {
            ...workingStyle,
            opacity: tween.working > 0.5 ? tween.working : 0,
          };
          const checkContainerStyle = {
            ...FULL_STYLE,
            pointerEvents: 'none',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          };
          const arrowStyleMerged = {
            ...arrowStyle,
            opacity: labelStyleMerged.opacity,
            left: `${arrowPadding + tween.arrow}rem`,
          };

          return (
            <button
              disabled={!enabled}
              style={buttonStyleMerged}
              onClick={onClick}
              type={type}
              ref={el => (this.buttonEl = el)}
            >
              <span ref={el => (this.labelEl = el)} style={labelStyleMerged}>
                {children}
              </span>
              <Arrow style={arrowStyleMerged} color={buttonStyleMerged.color} />
              <div style={workingContainerStyle}>
                <WorkingSpinner
                  color={buttonStyleMerged.color}
                  style={workingStyleMerged}
                  active={working}
                />
              </div>
              <div style={checkContainerStyle}>
                <Check
                  color={Color(colorA)
                    .alpha(tween.checked)
                    .string()}
                  style={{
                    maxWidth: buttonStyleMerged.maxWidth,
                    minWidth: buttonStyleMerged.minWidth,
                  }}
                />
              </div>
            </button>
          );
        }}
      </Motion>
    );
  }
}

export default withHover({ wrapper: 'span' })(Button);



// WEBPACK FOOTER //
// ./src/components/Button/Button.js